// src/components/Chat/MessageFormatter.ts
export class MessageFormatter {
  static formatMessage(content: string): string {
    if (!content) return "";

    // 1) Normalize line endings
    let t = content.replace(/\r\n/g, "\n");


    // Helpers to protect & restore code regions so we don't mangle them
    type SafeBlock = { ph: string; val: string };
    const fences: SafeBlock[] = [];
    const inlines: SafeBlock[] = [];
    const links: SafeBlock[] = [];

    const save = (arr: SafeBlock[], val: string, tag: "FENCE" | "INLINE" | "LINK") => {
      const ph = `@@_${tag}_${arr.length}_@@`;
      arr.push({ ph, val });
      return ph;
    };
    const restoreAll = (s: string, arr: SafeBlock[]) =>
      arr.reduce((acc, item) => acc.replace(item.ph, item.val), s);

    // 3) Protect fenced code blocks ```...```
    t = t.replace(/```([\s\S]*?)```/g, (_m, code) => {
      const block = "```" + code + "```";
      return save(fences, block, "FENCE");
    });

    // 4) Protect inline code `...`
    t = t.replace(/`([^`]+)`/g, (_m, code) => {
      const block = "`" + code + "`";
      return save(inlines, block, "INLINE");
    });

    // 4.5) Protect existing markdown link/image destinations ](...) so the
    // autolinking steps below don't re-wrap a URL or email that's already
    // a proper link target.
    t = t.replace(/\]\([^)]*\)/g, (m) => save(links, m, "LINK"));

    // 5) Inline formatting in Markdown-space (keep it minimal & safe)
    // 5a) Autolink bare URLs (wrap in <...>) without touching existing markdown links
    //     We’ll avoid matching if already inside [](...)
    t = t.replace(
      /(^|[\s(])((?:https?:\/\/|www\.)[^\s<)]+)(?=[\s)]|$)/gi,
      (_m, prefix: string, url: string) => {
        const m2 = url.match(/^(.*?)([.,;:!?)\]]*)$/);
        const clean = m2 ? m2[1] : url;
        const trail = m2 ? m2[2] : "";
        const href = /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
        return `${prefix}<${href}>${trail}`;
      }
    );

    // 5b) Autolink bare emails
    t = t.replace(
      /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
      (m) => `<mailto:${m}>`
    );

    // 6) Light whitespace cleanup around lists; keep user’s Markdown intact
    t = t
      .split("\n")
      .map((line) =>
        line.replace(/^(\s*[-*•])\s+/, (m, bullet) =>
          bullet === "•" ? "- " : `${bullet} `
        )
      )
      .join("\n");

    // 7) Restore inline & fenced code blocks, and protected link destinations
    t = restoreAll(t, inlines);
    t = restoreAll(t, fences);
    t = restoreAll(t, links);

    return t.trim();
  }

  static formatStructuredData(data: any): string {
    if (typeof data === "string") return this.formatMessage(data);
    if (data?.type === "employee_list") return this.formatEmployeeList(data.employees || []);
    if (data?.type === "project_teams") return this.formatProjectTeams(data.teams || []);
    if (data?.type === "document_analysis") return this.formatDocumentAnalysis(data.analysis || {});
    // Fallback: pretty-print JSON inside a fenced block
    return "```json\n" + JSON.stringify(data, null, 2) + "\n```";
  }

  // === Structured: Employees -> Markdown ===
  private static formatEmployeeList(employees: any[]): string {
    const parts: string[] = [];
    parts.push("### 👥 Employee Directory\n");
    employees.forEach((emp) => {
      const name = emp?.name ?? "Unknown";
      const id = emp?.id ?? "—";
      parts.push(`**${name}** _(ID: ${id})_`);
      const rows: string[] = [];
      if (emp?.department) rows.push(`- **Department:** ${emp.department}`);
      if (emp?.position) rows.push(`- **Position:** ${emp.position}`);
      if (emp?.status) rows.push(`- **Status:** ${emp.status}`);
      if (emp?.email) rows.push(`- **Email:** <mailto:${emp.email}>`);
      if (rows.length) parts.push(rows.join("\n"));
      parts.push(""); // blank line between cards
    });
    parts.push(`_Found ${employees.length} employee${employees.length === 1 ? "" : "s"}._`);
    return parts.join("\n");
  }

  // === Structured: Project Teams -> Markdown ===
  private static formatProjectTeams(teams: any[]): string {
    const parts: string[] = [];
    parts.push("### 🎯 Project Teams\n");
    teams.forEach((team) => {
      const name = team?.name ?? "Unnamed Team";
      const status = team?.status ? ` — **${team.status}**` : "";
      parts.push(`**${name}**${status}`);
      if (team?.project) parts.push(`- **Project:** ${team.project}`);
      if (team?.lead) parts.push(`- **Lead:** ${team.lead}`);
      if (Array.isArray(team?.members) && team.members.length) {
        parts.push(`- **Members:** ${team.members.map((m: string) => `\`${m}\``).join(", ")}`);
      }
      parts.push(""); // blank line between teams
    });
    return parts.join("\n");
  }

  // === Structured: Document Analysis -> Markdown ===
  private static formatDocumentAnalysis(analysis: any): string {
    const parts: string[] = [];
    parts.push("### 📊 Document Analysis\n");

    if (analysis?.summary) {
      parts.push("#### Summary");
      parts.push(this.formatMessage(String(analysis.summary)));
      parts.push("");
    }

    if (Array.isArray(analysis?.key_points) && analysis.key_points.length) {
      parts.push("#### Key Points");
      for (const p of analysis.key_points) {
        // Each key point may itself contain markdown—format it
        parts.push(`- ${this.formatMessage(String(p))}`);
      }
      parts.push("");
    }

    if (Array.isArray(analysis?.documents) && analysis.documents.length) {
      parts.push("#### Analyzed Documents");
      for (const d of analysis.documents) {
        const name = d?.name ?? "Untitled";
        const type = d?.type ? ` (${d.type})` : "";
        const pages = d?.pages ? ` — ${d.pages} pages` : "";
        parts.push(`- **${name}**${type}${pages}`);
      }
      parts.push("");
    }

    return parts.join("\n").trim();
  }
}
